import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Filter, X } from "lucide-react";
import { DraggableUser, AssignableUser } from "./DraggableUser";
import { FilterDropdown } from "./FilterDropdown";
import { AdminAPI } from "@shared/types/api";


interface Table {
  id: string;
  users: AssignableUser[];
}

interface UnassignedUsersAreaProps {
  users: AssignableUser[];
  activeUserId: string | null;
  overElementId: string | null;
  allTables: Table[];
  neighborhoodFilter: string;
  availableNeighborhoods?: string[];
  neighborhoodToCityMap?: Map<string, string>;
  onNeighborhoodFilterChange?: (value: string) => void;
  userHistory?: Record<string, AdminAPI.UserHistory>;
  onEditUser?: (userId: string) => void;
  onSendEmail?: (userId: string) => void;
  onReassign?: (userId: string) => void;
  onNeighborhoodClick?: (neighborhood: string) => void;
}

export function UnassignedUsersArea({
  users,
  activeUserId,
  overElementId,
  allTables,
  neighborhoodFilter,
  availableNeighborhoods = [],
  neighborhoodToCityMap,
  onNeighborhoodFilterChange,
  userHistory,
  onEditUser,
  onSendEmail,
  onReassign,
  onNeighborhoodClick,
}: UnassignedUsersAreaProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: "unassigned-area",
  });

  // Check if we should show hover state
  const sourceTable = allTables.find((t) =>
    t.users.some((u) => u.id === activeUserId)
  );
  const isDraggingFromTable = activeUserId && sourceTable;
  const isHoveringOverUnassignedUser =
    overElementId && users.some((u) => u.id === overElementId);
  const shouldShowHover =
    isDraggingFromTable && (isOver || isHoveringOverUnassignedUser);

  return (
    <div
      ref={setNodeRef}
      className={`card sticky top-24 transition-all duration-200 ${
        shouldShowHover
          ? "ring-2 ring-accent-500 ring-offset-2 ring-offset-dark-bg bg-accent-500/5"
          : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="text-xl font-bold flex flex-row space-between w-full">
          <div className={"px-2"}>Total {users.length}</div>
          {onNeighborhoodFilterChange && availableNeighborhoods.length > 0 && (
          <div className="flex items-center gap-1">
            <FilterDropdown
              label="Neighborhood"
              icon={<Filter className="w-3.5 h-3.5" />}
              value={neighborhoodFilter}
              options={[
                { value: "all", label: "All Neighborhoods" },
                ...[...availableNeighborhoods]
                  .sort((a, b) => {
                    const cityA = neighborhoodToCityMap?.get(a.toLowerCase()) || "";
                    const cityB = neighborhoodToCityMap?.get(b.toLowerCase()) || "";
                    const cityCompare = cityA.localeCompare(cityB);
                    if (cityCompare !== 0) return cityCompare;
                    return a.localeCompare(b);
                  })
                  .map((n) => {
                    const cityName = neighborhoodToCityMap?.get(n.toLowerCase());
                    const neighborhoodLabel = n.charAt(0).toUpperCase() + n.slice(1);
                    return {
                      value: n,
                      label: cityName ? `${cityName} - ${neighborhoodLabel}` : neighborhoodLabel,
                    };
                  }),
              ]}
              onChange={onNeighborhoodFilterChange}
            />
            {neighborhoodFilter !== "all" && (
              <button
                type="button"
                onClick={() => onNeighborhoodFilterChange("all")}
                className="btn btn-sm btn-ghost btn-circle"
                title="Clear neighborhood filter"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        </div>
        
      </div>
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        <SortableContext
          items={users.map((u) => u.id)}
          strategy={verticalListSortingStrategy}
        >
          {users.map((user) => (
            <DraggableUser
              key={user.id}
              user={user}
              history={userHistory?.[user.id]}
              onEdit={onEditUser}
              onSendEmail={onSendEmail}
              onReassign={onReassign}
              onNeighborhoodClick={onNeighborhoodClick}
            />
          ))}
        </SortableContext>
        {users.length === 0 && (
          <div
            className={`text-center py-8 transition-all duration-200 ${
              shouldShowHover ? "text-accent-500 scale-105" : "text-dark-muted"
            }`}
          >
            {neighborhoodFilter === "all"
              ? shouldShowHover
                ? "Drop here to unassign"
                : "All users assigned! 🎉"
              : `No unassigned users in ${neighborhoodFilter}`}
          </div>
        )}
      </div>
    </div>
  );
}

